import type { TranslationKey } from '@/i18n-key';
import type { Company } from '@/types/directory/Company';
import type { ObjectiveParticipantAttribute } from '@/types/objectives';
import type { LocalizedText } from '@/utils/localization';
import type { Icon } from '@amelio/ui-library';
import type { TFunction } from 'i18next';
import type { ComponentProps } from 'react';

import { Filters } from '@/constants/Filters';
import {
    ObjectiveParticipantAttributeType,
    type ObjectiveParticipantAttributeTypeValue,
} from '@/constants/ObjectiveParticipantAttributeType';
import { getCompanyAttributeList } from '@/utils/company';
import { decodeAndSanitize } from '@/utils/htmlSanitizer';
import { getLocalizedText } from '@/utils/localization';

type IconName = ComponentProps<typeof Icon>['name'];

/** The icons the company attribute lists already give each family (`getCompanyAttributeList`). */
export const PARTICIPANT_ATTRIBUTE_ICONS: Record<ObjectiveParticipantAttributeTypeValue, IconName> =
    {
        [ObjectiveParticipantAttributeType.DEPARTMENT]: 'building-building-4',
        [ObjectiveParticipantAttributeType.JOB_TITLE]: 'school-briefcase',
        [ObjectiveParticipantAttributeType.COMPANY_ADDRESS]: 'location-location',
        [ObjectiveParticipantAttributeType.EMPLOYEE_LEVEL]: 'design-layer',
        [ObjectiveParticipantAttributeType.MANAGER]: 'users-user',
        [ObjectiveParticipantAttributeType.CUSTOM_ATTRIBUTE]: 'school-bookmark',
    };

/** The `getCompanyAttributeList` key each participant family answers to -- the same keys the
 * engagement-style `company.attributes` entries carry. */
const TYPE_TO_ATTRIBUTE_KEY: Partial<Record<ObjectiveParticipantAttributeTypeValue, string>> = {
    [ObjectiveParticipantAttributeType.DEPARTMENT]: Filters.DEPARTMENTS,
    [ObjectiveParticipantAttributeType.JOB_TITLE]: Filters.JOB_TITLES,
    [ObjectiveParticipantAttributeType.COMPANY_ADDRESS]: Filters.COMPANY_ADDRESSES,
    [ObjectiveParticipantAttributeType.EMPLOYEE_LEVEL]: Filters.EMPLOYEE_LEVELS,
    [ObjectiveParticipantAttributeType.MANAGER]: Filters.MANAGERS,
};

interface ParticipantAttributeOption {
    attribute: ObjectiveParticipantAttribute;
    name: string;
}

export interface ParticipantAttributeGroup {
    /** The family, or the custom attribute's own id: unique within the company. */
    key: string;
    /** Heads the family's values in the picker. */
    heading: string;
    /** Names the family beside one of its values. */
    familyName: string;
    icon: IconName;
    options: ParticipantAttributeOption[];
}

interface NamedValue {
    id: string;
    name: string;
}

const localized = (entries: { id: string; names: LocalizedText }[] | undefined): NamedValue[] =>
    (entries ?? []).map(({ id, names }) => ({ id, name: getLocalizedText(names) }));

/** Engagement's order for the audience attributes; age and tenure are never offered. */
const STANDARD_FAMILIES: {
    type: ObjectiveParticipantAttributeTypeValue;
    headingKey: TranslationKey;
    familyKey: TranslationKey;
    values: (company: Company) => NamedValue[];
}[] = [
    {
        type: ObjectiveParticipantAttributeType.DEPARTMENT,
        headingKey: 'objectives.form.participantPicker.groups.departments',
        familyKey: 'common.department',
        values: (company) => localized(company.departments),
    },
    {
        type: ObjectiveParticipantAttributeType.JOB_TITLE,
        headingKey: 'objectives.form.participantPicker.groups.jobTitles',
        familyKey: 'common.jobTitle',
        values: (company) => localized(company.jobTitles),
    },
    {
        type: ObjectiveParticipantAttributeType.COMPANY_ADDRESS,
        headingKey: 'objectives.form.participantPicker.groups.locations',
        familyKey: 'common.office',
        values: (company) => localized(company.companyAddresses),
    },
    {
        type: ObjectiveParticipantAttributeType.EMPLOYEE_LEVEL,
        headingKey: 'objectives.form.participantPicker.groups.employeeLevels',
        familyKey: 'common.employeeLevel',
        values: (company) => localized(company.employeeLevels),
    },
    {
        type: ObjectiveParticipantAttributeType.MANAGER,
        headingKey: 'objectives.form.participantPicker.groups.managers',
        familyKey: 'common.manager',
        values: (company) => (company.managers ?? []).map(({ id, name }) => ({ id, name })),
    },
];

const toOptions = (
    type: ObjectiveParticipantAttributeTypeValue,
    values: NamedValue[],
): ParticipantAttributeOption[] =>
    values.map(({ id, name }) => ({ attribute: { type, valueId: id }, name }));

/** The company's attribute values, one group per family, then one per custom attribute. */
export const buildParticipantAttributeGroups = (
    company: Company,
    t: TFunction,
): ParticipantAttributeGroup[] => {
    const attributeMap = getCompanyAttributeList(company);
    // Only a key the payload itself carries renames a family: `attributeMap` names every base
    // family from this app's own translations, and taking those unconditionally would turn
    // "Team of" into "Manager" and "Locations" into "Office" even with nothing configured.
    const dynamicKeys = new Set(
        (company.attributes ?? []).map(
            (attribute) => (attribute as unknown as { key?: string }).key ?? attribute.id,
        ),
    );
    const dynamicName = (type: ObjectiveParticipantAttributeTypeValue): string | undefined => {
        const attributeKey = TYPE_TO_ATTRIBUTE_KEY[type];
        const name =
            attributeKey && dynamicKeys.has(attributeKey)
                ? attributeMap[attributeKey]?.name
                : undefined;

        return name ? decodeAndSanitize(name) : undefined;
    };

    return [
        ...STANDARD_FAMILIES.map(({ type, headingKey, familyKey, values }) => {
            const name = dynamicName(type);

            return {
                key: type,
                heading: name ?? t(headingKey),
                familyName: name ?? t(familyKey),
                icon: PARTICIPANT_ATTRIBUTE_ICONS[type],
                options: toOptions(type, values(company)),
            };
        }),
        ...(company.customAttributes ?? []).map((customAttribute) => {
            const name = getLocalizedText(customAttribute.names);

            return {
                key: customAttribute.id,
                heading: name,
                familyName: name,
                icon: PARTICIPANT_ATTRIBUTE_ICONS[
                    ObjectiveParticipantAttributeType.CUSTOM_ATTRIBUTE
                ],
                options: toOptions(
                    ObjectiveParticipantAttributeType.CUSTOM_ATTRIBUTE,
                    localized(customAttribute.values),
                ),
            };
        }),
    ];
};

/** The groups with only the "Team of" values whose manager is in `managerIds`. */
export const narrowTeamsTo = (
    groups: ParticipantAttributeGroup[],
    managerIds: ReadonlySet<string>,
): ParticipantAttributeGroup[] =>
    groups.map((group) =>
        group.key === ObjectiveParticipantAttributeType.MANAGER
            ? {
                  ...group,
                  options: group.options.filter((option) =>
                      managerIds.has(option.attribute.valueId),
                  ),
              }
            : group,
    );

export const isSameParticipantAttribute = (
    a: ObjectiveParticipantAttribute,
    b: ObjectiveParticipantAttribute,
): boolean => a.type === b.type && a.valueId === b.valueId;

/** `"<Type>:<ValueId>"`, as the discussion journal writes an attribute value. */
export const participantAttributeKey = ({ type, valueId }: ObjectiveParticipantAttribute): string =>
    `${type}:${valueId}`;

export interface IndexedParticipantAttribute {
    group: ParticipantAttributeGroup;
    option: ParticipantAttributeOption;
}

/** Every value the company still has, with its group, keyed by `participantAttributeKey`. */
export const indexParticipantAttributes = (
    groups: ParticipantAttributeGroup[],
): ReadonlyMap<string, IndexedParticipantAttribute> =>
    new Map(
        groups.flatMap((group) =>
            group.options.map((option) => [
                participantAttributeKey(option.attribute),
                { group, option },
            ]),
        ),
    );

const normalizeForSearch = (value: string): string =>
    value
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase();

/**
 * The values whose name holds the search, case and accents aside, less the values already chosen.
 * A group left without a value is dropped.
 */
export const filterParticipantAttributeGroups = (
    groups: ParticipantAttributeGroup[],
    search: string,
    chosen: ObjectiveParticipantAttribute[],
): ParticipantAttributeGroup[] => {
    const needle = normalizeForSearch(search.trim());

    return groups
        .map((group) => ({
            ...group,
            options: group.options.filter(
                (option) =>
                    !chosen.some((attribute) =>
                        isSameParticipantAttribute(attribute, option.attribute),
                    ) && normalizeForSearch(option.name).includes(needle),
            ),
        }))
        .filter((group) => group.options.length > 0);
};
